import { useState } from 'react'

import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Button } from 'components/Button/Button'
import InputCard from 'components/Form/InputCard/InputCard'
import { addToast } from 'components/Toast/Toast'
import {
    resendSecurityRecovery,
    resetPassword,
    sendPasswordResetEmail
} from 'services/endpoints/auth'
import {
    useSetUserProfile
} from 'store'
import { useLocalStorageSetAccessToken } from 'utils'

interface ResetPasswordProps {
    setActivePanel?: (panel: string) => void
}

export const ResetPassword = ({
    setActivePanel
}: ResetPasswordProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const queryClient = useQueryClient()
    const token =
        searchParams.get('token') ?? ''

    const email =
        searchParams.get('email') ?? ''

    const isSecurityRecovery =
        searchParams.get(
            'security_recovery'
        ) === '1'

    const [password, setPassword] =
        useState('')

    const [
        passwordConfirmation,
        setPasswordConfirmation
    ] = useState('')

    const [submitting, setSubmitting] =
        useState(false)

    const [linkExpired, setLinkExpired] =
        useState(false)

    const [resending, setResending] =
        useState(false)

    const validPassword = (
        value: string
    ): boolean => {
        return (
            value.length >= 8 &&
            /[a-z]/.test(value) &&
            /[A-Z]/.test(value) &&
            /[0-9]/.test(value)
        )
    }

    const resendLink = async () => {
        if (
            !email ||
            resending
        ) {
            return
        }

        setResending(true)

        try {
            if (isSecurityRecovery) {
                await resendSecurityRecovery({
                    email
                })
            } else {
                await sendPasswordResetEmail({
                    email
                })
            }

            addToast(
                'success',
                isSecurityRecovery
                    ? 'A new security recovery email has been sent.'
                    : 'A new password reset email has been sent.'
            )
        } catch {
            addToast(
                'error',
                'Unable to send a new recovery email.'
            )
        } finally {
            setResending(false)
        }
    }

const submitForm = async () => {
    if (!token || !email) {
        setLinkExpired(true)

        addToast(
            'error',
            'This password reset link is invalid.'
        )

        return
    }

    if (!validPassword(password)) {
        addToast(
            'error',
            'Password must contain at least 8 characters, including uppercase and lowercase letters, and a number.'
        )

        return
    }

    if (
        password !==
        passwordConfirmation
    ) {
        addToast(
            'error',
            'Passwords do not match.'
        )

        return
    }

    if (submitting) return

    setSubmitting(true)

    try {
        const response =
            await resetPassword({
                token,
                email,
                password,
                password_confirmation:
                    passwordConfirmation
            })

        addToast(
            'success',
            'Your password has been reset successfully.'
        )

        /*
         * Security compromise recovery returns a fresh
         * Sanctum token + profile so the recovered user
         * can continue immediately.
         */
        if (
            isSecurityRecovery &&
            response?.token &&
            response?.userProfile
        ) {
            useLocalStorageSetAccessToken(
                response.token
            )

            useSetUserProfile(
                response.userProfile
            )

            queryClient.setQueryData(
                ['user-profile'],
                response.userProfile
            )

            if (
                response.userProfile.role ===
                    'model' &&
                response.userProfile.username
            ) {
                navigate(
                    `/${response.userProfile.username}/`,
                    {
                        replace: true
                    }
                )

                return
            }

            navigate('/following', {
                replace: true
            })

            return
        }

        /*
         * Normal forgot-password flow:
         * password changed, but user still needs to log in.
         */
        navigate('/', {
            replace: true
        })

        setActivePanel?.('login')
    } catch (error) {
        if (
            axios.isAxiosError(error)
        ) {
            const response =
                error.response?.data

            const errors =
                response?.errors ??
                (
                    typeof response?.message ===
                    'object'
                        ? response.message
                        : null
                )

            if (
                errors?.email?.length
            ) {
                const message =
                    String(
                        errors.email[0]
                    )

                setLinkExpired(true)

                addToast(
                    'error',
                    message
                )

                return
            }

            if (
                errors?.password?.length
            ) {
                addToast(
                    'error',
                    String(
                        errors.password[0]
                    )
                )

                return
            }

            if (
                typeof response?.message ===
                'string'
            ) {
                const message =
                    response.message

                const normalized =
                    message.toLowerCase()

                if (
                    normalized.includes(
                        'token'
                    ) ||
                    normalized.includes(
                        'expired'
                    ) ||
                    normalized.includes(
                        'invalid'
                    ) ||
                    normalized.includes(
                        'no longer valid'
                    )
                ) {
                    setLinkExpired(true)
                }

                addToast(
                    'error',
                    message
                )

                return
            }
        }

        addToast(
            'error',
            'Unable to reset password.'
        )
    } finally {
        setSubmitting(false)
    }
}

    return (
        <div className='auth-recover'>
            <h1 className='auth-recover__title'>
                {t('resetPassword')}
            </h1>

            <p className='auth-recover__text'>
                Enter a new password for your account.
            </p>

            <div className='auth-recover__form'>
                <InputCard
                    type='password'
                    label={t('newPassword')}
                    value={password}
                    changeFn={(
                        val: string
                    ) => {
                        setPassword(val)
                    }}
                    hasTypeChanger={true}
                    validate='password'
                    hideLabelWhenFilled={true}
                />

                <InputCard
                    type='password'
                    label={t(
                        'confirmNewPassword'
                    )}
                    value={
                        passwordConfirmation
                    }
                    changeFn={(
                        val: string
                    ) => {
                        setPasswordConfirmation(
                            val
                        )
                    }}
                    hasTypeChanger={true}
                    validate='password'
                    hideLabelWhenFilled={true}
                />

                <div className='password-requirements'>
                    The password must contain at least 8
                    characters, including uppercase and
                    lowercase letters, and a number.
                </div>

                {linkExpired && (
                    <div className='auth-recover__expired'>
                        <p>
                            This password reset link is no longer valid.
                        </p>

                        <Button
                            text={
                                resending
                                    ? 'Sending...'
                                    : isSecurityRecovery
                                        ? 'Send New Recovery Email'
                                        : 'Send New Password Reset Email'
                            }
                            color='transparent'
                            type='transparent--black'
                            font='mont-15-semi-bold'
                            width='100'
                            height='5'
                            disabled={resending}
                            clickFn={resendLink}
                        />
                    </div>
                )}

                <div className='auth-recover__buttons'>
                    <Button
                        text={t(
                            'resetPassword'
                        )}
                        color='blue'
                        font='mont-15-semi-bold'
                        width='100'
                        height='5'
                        clickFn={submitForm}
                    />

                    <Button
                        text={t('cancel')}
                        color='transparent'
                        type='transparent--black'
                        font='mont-15-semi-bold'
                        width='100'
                        height='5'
                        clickFn={() => {
                            navigate('/', {
                                replace: true
                            })

                            setActivePanel?.(
                                'login'
                            )
                        }}
                    />
                </div>
            </div>
        </div>
    )
}